import type { jsPDF } from 'jspdf';
import { MONTH_ORDER, MONTH_FULL_NAMES, ReportFilters } from '../../types/types';
import { colors, fonts, layout } from '../../theme/themes';
import { formatReportId } from '../../utils/summary_report/reportId';

type RGB = [number, number, number];

/**
 * Brand palette for the header band. Values come from the web header layout.
 * Move these into ../theme/themes if you want them shared with the rest of the report.
 */
const BRAND = {
  navy: [23, 50, 77] as RGB, // #17324D
  orange: [245, 131, 26] as RGB, // #F5831A
  white: [255, 255, 255] as RGB,
  /** Fill for the block after the orange accent (report label + period).
   * Pick your own shade — kept dark enough that the existing white /
   * soft-white text in that block stays legible without changing color.
   * Go lighter than roughly [140,140,140] and you'll want to flip that
   * block's text to a dark color instead. */
  grey: [71, 85, 105] as RGB, // slate-600
};

/** Corner radius for the navy/grey band. jsPDF's roundedRect rounds all
 * 4 corners of one shape — see the grey-panel inset below for why the
 * grey fill doesn't just extend flush to the band's edges. */
const HEADER_RADIUS = 5;

/** Blends `from` toward `to` (t = 0..1). Used instead of PDF opacity so it renders identically everywhere. */
function mix(from: RGB, to: RGB, t: number): RGB {
  return from.map((c, i) => Math.round(c + (to[i] - c) * t)) as RGB;
}

const TINT = {
  textSoft: mix(BRAND.navy, BRAND.white, 0.6), // "text-white/60"
  divider: mix(BRAND.navy, BRAND.white, 0.3), // "bg-white/30"
  placeholder: mix(BRAND.navy, BRAND.white, 0.35),
};

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function lastDayOfMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Builds the "Jan 1 – Sep 30, 2026" style period string from the report filters. */
function periodLabel(filters: ReportFilters): string {
  const { year, month, mode } = filters;
  if (mode === 'year' || !month) {
    return `Jan 1 – Dec 31, ${year}`;
  }
  const monthIndex = MONTH_ORDER.indexOf(month);
  const lastDay = lastDayOfMonth(year, monthIndex);
  if (mode === 'month') {
    return `${month} 1 – ${month} ${lastDay}, ${year}`;
  }
  // mode === 'both': year-to-date through the end of the selected month
  return `Jan 1 – ${month} ${lastDay}, ${year}`;
}

/** Builds the "September 2026 report" / "2026 report" label shown at the right. */
function reportLabel(filters: ReportFilters): string {
  if (filters.mode === 'year' || !filters.month) {
    return `${filters.year} report`;
  }
  return `${MONTH_FULL_NAMES[filters.month]} ${filters.year} Report`;
}

export interface ReportHeaderOptions {
  x: number;
  y: number;
  width: number;
  filters: ReportFilters;
  title?: string;
  /**
   * Base64 image data URIs. Use white / reversed artwork — they sit on the navy band.
   * logoLeft  → organisation logo (left edge).
   * logoRight → programme / protection icon (next to the title, after the divider).
   * Omitted logos render as dashed placeholder boxes.
   */
  logoLeft?: string;
  logoRight?: string;
  now?: Date; // injectable for testing; defaults to the actual generation time
  preparedBy?: string;
  reportId?: string;
}

/**
 * Draws the report's top header and returns the next y-cursor.
 *
 *  ┌──────────────────────────────────────────────────────────────┐
 *  │ [logo] │ [icon] Title                    ▰   September 2026 report │  navy band
 *  │                 Prepared by …                 Sep 1 – Sep 30, 2026 │
 *  └──────────────────────────────────────────────────────────────┘
 *  Generated Sep 19, 2026                                    Ref: …
 *  ─────────────────────────────────────────────────────────────
 */
export function drawReportHeader(doc: jsPDF, opts: ReportHeaderOptions): number {
  const { x, y, width, filters, logoLeft, logoRight } = opts;
  const title = opts.title ?? 'Eviction Risk & Response Update';
  const now = opts.now ?? new Date();
  const preparedBy = opts.preparedBy ?? 'NRC Somalia \u2014 HLP AoR';
  const reportId = opts.reportId ?? formatReportId(now);

  const bandH = 62;
  const padX = 22;
  const centerY = y + bandH / 2;
  const bandBottom = y + bandH;

  // ---- helpers -----------------------------------------------------------

  /** Aspect-fits an image inside maxW × maxH; placeholders use the fallback width. */
  const logoSize = (image: string | undefined, maxW: number, maxH: number, placeholderW: number) => {
    if (!image) return { w: placeholderW, h: maxH };
    try {
      const p = doc.getImageProperties(image);
      const s = Math.min(maxW / p.width, maxH / p.height);
      return { w: p.width * s, h: p.height * s };
    } catch {
      return { w: placeholderW, h: maxH };
    }
  };

  const drawLogo = (image: string | undefined, lx: number, size: { w: number; h: number }) => {
    const ly = centerY - size.h / 2;
    if (image) {
      doc.addImage(image, lx, ly, size.w, size.h);
      return;
    }
    doc.setDrawColor(...TINT.placeholder);
    doc.setLineDashPattern([2, 1.5], 0);
    doc.setLineWidth(0.6);
    doc.roundedRect(lx, ly, size.w, size.h, 3, 3, 'D');
    doc.setLineDashPattern([], 0);
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...TINT.placeholder);
    doc.text('Logo', lx + size.w / 2, ly + size.h / 2 + 2, { align: 'center' });
  };

  /** Shrinks a font size until `text` fits `maxW` (or `min` is reached). */
  const fitFontSize = (
    text: string,
    family: string,
    style: string,
    start: number,
    min: number,
    maxW: number,
  ) => {
    doc.setFont(family, style);
    let size = start;
    doc.setFontSize(size);
    while (size > min && doc.getTextWidth(text) > maxW) {
      size -= 0.5;
      doc.setFontSize(size);
    }
    return size;
  };

  // ---- layout (measure everything before drawing) -------------------------

  // Left cluster: logo | divider | icon | title block
  const nrc = logoSize(logoLeft, 110, 32, 92);
  const icon = logoSize(logoRight, 28, 28, 28);
  const nrcX = x + padX;
  const dividerX = nrcX + nrc.w + 14;
  const iconX = dividerX + 14;
  const textX = iconX + icon.w + 10;

  // Right block: report label + period, right-aligned
  const rightLabel = reportLabel(filters);
  const rightPeriod = periodLabel(filters);
  doc.setFont(fonts.voice, 'bold');
  doc.setFontSize(9.5);
  const rightLabelW = doc.getTextWidth(rightLabel);
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(8);
  const rightPeriodW = doc.getTextWidth(rightPeriod);
  const rightW = Math.max(rightLabelW, rightPeriodW);
  const rightEdge = x + width - padX;

  // Angled orange accent sits just left of the right block, so it never collides with the text.
  const stripeW = 16;
  const stripeX = rightEdge - rightW - 14 - stripeW;

  // Title: shrink to fit, then wrap to two lines if it still doesn't.
  const textMaxW = stripeX - 12 - textX;
  const titleSize = fitFontSize(title, fonts.voice, 'bold', 12.5, 10, textMaxW);
  const titleLines: string[] =
    doc.getTextWidth(title) > textMaxW ? doc.splitTextToSize(title, textMaxW) : [title];
  const titleLeading = titleSize * 1.25;

  const subSize = fitFontSize(preparedBy, fonts.body, 'normal', 7.5, 6, textMaxW);

  // Vertically centre the title + subtitle block in the band.
  const blockH = titleLines.length * titleLeading + 4 + subSize;
  const firstBaseline = centerY - blockH / 2 + titleSize * 0.85;
  const subBaseline = firstBaseline + (titleLines.length - 1) * titleLeading + subSize + 4;

  // ---- draw --------------------------------------------------------------

  // ---- Navy + grey band, split along the same diagonal as the orange
  // accent, clipped to the band's rounded outer corners. ---------------
  //
  // Why not two plain rects: the accent's edge is diagonal (it slants from
  // x = stripeX + stripeW*0.55 at the top to x = stripeX at the bottom), so
  // a straight vertical seam between navy and grey would leave a small
  // mismatched triangle showing at either the top or the bottom of the
  // band. Using that same diagonal as the navy/grey boundary makes the two
  // fills tile the band exactly, with the orange painted on top bridging
  // them — a true seamless transition, not a panel dropped on top.
  //
  // jsPDF only rounds all 4 corners of one shape at a time, so the navy
  // and grey trapezoids below are sharp-cornered on purpose (that internal
  // seam should be sharp) and get their outer corners rounded for free by
  // clipping everything to a rounded-rect path first.
  const seamTopX = stripeX + stripeW * 0.55; // matches the accent's own top-left vertex
  const seamBottomX = stripeX; // matches the accent's own bottom-left vertex

  doc.saveGraphicsState();
  doc.roundedRect(x, y, width, bandH, HEADER_RADIUS, HEADER_RADIUS, null); // path only — clipped below, never painted
  doc.clip();
  doc.discardPath();

  doc.setFillColor(...BRAND.navy);
  doc.lines(
    [
      [seamTopX - x, 0],
      [seamBottomX - seamTopX, bandH],
      [x - seamBottomX, 0],
    ],
    x,
    y,
    [1, 1],
    'F',
    true,
  );

  doc.setFillColor(...BRAND.grey);
  doc.lines(
    [
      [x + width - seamTopX, 0],
      [0, bandH],
      [seamBottomX - (x + width), 0],
    ],
    seamTopX,
    y,
    [1, 1],
    'F',
    true,
  );

  // Angled orange accent — same polygon as the web layout: (55% 0, 100% 0, 45% 100%, 0 100%)
  doc.setFillColor(...BRAND.orange);
  doc.lines(
    [
      [stripeW * 0.45, 0],
      [-stripeW * 0.55, bandH],
      [-stripeW * 0.45, 0],
    ],
    stripeX + stripeW * 0.55,
    y,
    [1, 1],
    'F',
    true,
  );

  doc.restoreGraphicsState(); // end the rounded-corner clip started above

  // Logo | divider | icon
  drawLogo(logoLeft, nrcX, nrc);
  doc.setDrawColor(...TINT.divider);
  doc.setLineWidth(0.75);
  doc.line(dividerX, centerY - 16, dividerX, centerY + 16);
  drawLogo(logoRight, iconX, icon);

  // Title + prepared-by
  doc.setFont(fonts.voice, 'bold');
  doc.setFontSize(titleSize);
  doc.setTextColor(...BRAND.white);
  titleLines.forEach((line, i) => doc.text(line, textX, firstBaseline + i * titleLeading));

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(subSize);
  doc.setTextColor(...TINT.textSoft);
  doc.text(preparedBy, textX, subBaseline);

  // Right block: report label + period
  doc.setFont(fonts.voice, 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND.white);
  doc.text(rightLabel, rightEdge, centerY - 3, { align: 'right' });

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...TINT.textSoft);
  doc.text(rightPeriod, rightEdge, centerY + 8.5, { align: 'right' });

  /* Meta strip under the band: generation date left, reference right, hairline below.
  const metaBaseline = bandBottom + 13;
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.stone500);
  doc.text(`Generated ${formatDate(now)}`, x, metaBaseline);
  doc.text(`Ref: ${reportId}`, x + width, metaBaseline, { align: 'right' });
  */

  const ruleY = bandBottom;
  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.line(x, ruleY, x + width, ruleY);

  return ruleY + layout.cardGap * 2; // breathing room before section 01
}